// ---------------------------------------------------------------------------
// Data Engineering intake form model + ticket-summary builder.
//
// Fields mirror the "Data Engineering Intake Form" workbook (7 sections,
// ★ = required). buildDescription renders the workbook's "Ticket Summary":
// only filled fields, grouped by section.
// ---------------------------------------------------------------------------

/** Format a Date as M/D/YYYY for display and email bodies. */
export function formatDate(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`
}

export type Priority = 'low' | 'medium' | 'high'

export interface IntakeValues {
  // Subject (appended to "Prism Portal Request - ")
  subject: string
  // 1. Request Info
  requestor: string
  project: string
  businessOwner: string
  priority: Priority
  goLive: string
  // 2. Business Context
  problemStatement: string
  businessImpact: string
  useCase: string
  // 3. Source Details
  sourceSystem: string
  dataFormat: string
  frequency: string
  sourceLocation: string
  sourceContact: string
  // 4. Landing / Bronze
  landingPath: string
  bronzeTable: string
  retention: string
  schemaEnforcement: string
  bronzeNotes: string
  // 5. Transformations / Silver
  cleansing: string
  deduplication: string
  businessRules: string
  targetTable: string
  // 6. Gold / Consumption
  outputTable: string
  consumers: string
  aggregations: string
  // 7. Data Quality & Governance
  pii: string
  validationRules: string
  access: string
}

export const INITIAL_VALUES: IntakeValues = {
  subject: '',
  requestor: '',
  project: '',
  businessOwner: '',
  priority: 'medium',
  goLive: formatDate(new Date()),
  problemStatement: '',
  businessImpact: '',
  useCase: '',
  sourceSystem: '',
  dataFormat: '',
  frequency: '',
  sourceLocation: '',
  sourceContact: '',
  landingPath: '',
  bronzeTable: '',
  retention: '',
  schemaEnforcement: '',
  bronzeNotes: '',
  cleansing: '',
  deduplication: '',
  businessRules: '',
  targetTable: '',
  outputTable: '',
  consumers: '',
  aggregations: '',
  pii: '',
  validationRules: '',
  access: '',
}

export function linearPriority(priority: Priority): number {
  switch (priority) {
    case 'high':
      return 2
    case 'medium':
      return 3
    case 'low':
    default:
      return 4
  }
}

// Section -> ordered [valueKey, label] pairs, used to build the ticket summary
// (only filled fields are emitted).
const SUMMARY_SECTIONS: {
  title: string
  fields: [keyof IntakeValues, string][]
}[] = [
  {
    title: 'REQUEST INFO',
    fields: [
      ['requestor', 'Requestor'],
      ['project', 'Project / Initiative'],
      ['businessOwner', 'Business Owner'],
      ['priority', 'Priority'],
      ['goLive', 'Requested Go-Live'],
    ],
  },
  {
    title: 'BUSINESS CONTEXT',
    fields: [
      ['problemStatement', 'Problem Statement'],
      ['businessImpact', 'Business Impact'],
      ['useCase', 'Use Case / Scenario'],
    ],
  },
  {
    title: 'SOURCE DETAILS',
    fields: [
      ['sourceSystem', 'Source System'],
      ['dataFormat', 'Data Format'],
      ['frequency', 'Frequency'],
      ['sourceLocation', 'Source Location'],
      ['sourceContact', 'Source Contact'],
    ],
  },
  {
    title: 'LANDING / BRONZE',
    fields: [
      ['landingPath', 'Landing Path'],
      ['bronzeTable', 'Bronze Table Name'],
      ['retention', 'Retention'],
      ['schemaEnforcement', 'Schema Enforcement'],
      ['bronzeNotes', 'Notes'],
    ],
  },
  {
    title: 'TRANSFORMATIONS / SILVER',
    fields: [
      ['cleansing', 'Cleansing'],
      ['deduplication', 'Deduplication'],
      ['businessRules', 'Business Rules'],
      ['targetTable', 'Target Table'],
    ],
  },
  {
    title: 'GOLD / CONSUMPTION',
    fields: [
      ['outputTable', 'Output Table'],
      ['consumers', 'Consumers'],
      ['aggregations', 'Aggregations'],
    ],
  },
  {
    title: 'DATA QUALITY & GOVERNANCE',
    fields: [
      ['pii', 'PII / Sensitive Data'],
      ['validationRules', 'Validation Rules'],
      ['access', 'Access'],
    ],
  },
]

// Where intake requests are emailed, and the subject prefix.
export const REQUEST_RECIPIENT = 'enterprisedata@loandepot.com'
export const REQUEST_SUBJECT_PREFIX = 'Prism Portal Request'

// Plain-text email body: only filled fields, grouped by section. Uses CRLF
// line breaks, which mail clients handle most consistently for mailto bodies.
export function buildEmailBody(values: IntakeValues): string {
  const blocks: string[] = []
  for (const section of SUMMARY_SECTIONS) {
    const filled = section.fields.filter(
      ([key]) => String(values[key] ?? '').trim().length > 0
    )
    if (filled.length === 0) continue
    const lines = [section.title]
    for (const [key, label] of filled) {
      lines.push(`  ${label}: ${String(values[key]).trim()}`)
    }
    blocks.push(lines.join('\r\n'))
  }
  return blocks.join('\r\n\r\n')
}

// Outlook on the web "compose" deep link. Opening this pops a prefilled
// Outlook compose window regardless of the OS default mail handler, so the
// request is authored in Outlook (M365 users are already signed in).
export const OUTLOOK_COMPOSE_BASE =
  'https://outlook.office.com/mail/deeplink/compose'

/** Build the full subject line, appending the user's subject to the prefix. */
export function buildSubject(subject: string): string {
  const trimmed = subject.trim()
  return trimmed
    ? `${REQUEST_SUBJECT_PREFIX} - ${trimmed}`
    : REQUEST_SUBJECT_PREFIX
}

export function buildOutlookComposeUrl(
  values: IntakeValues,
  loginHint?: string | null
): string {
  const params = new URLSearchParams({
    to: REQUEST_RECIPIENT,
    subject: buildSubject(values.subject),
    body: buildEmailBody(values),
  })
  // Target the same account the user is signed into Prism with, so Outlook
  // opens under their identity rather than whatever session the browser has.
  if (loginHint && loginHint.trim()) {
    params.set('login_hint', loginHint.trim())
  }
  // Encode spaces as %20 (not "+") so the subject/body render literally.
  const query = params.toString().replace(/\+/g, '%20')
  return `${OUTLOOK_COMPOSE_BASE}?${query}`
}
